import React, { useState } from 'react';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  FileText,
  Brain,
  MessageSquare,
  Trash2,
  Info,
  Check,
  ChevronRight,
  ChevronDown,
  File,
  Edit2,
  Save,
  Activity,
  Flag,
  Sparkles,
  Pencil,
  History,
  X,
  ArrowRight,
  Hash,
  Clock,
  Lock,
  CalendarDays,
} from 'lucide-react';
import { ThreadStats } from './ChatComponents';
import { copyText } from '../utils/clipboard';
import { formatDateKo } from '../utils/date';
import type { ThreadDetail } from '../api/threads';
import type { HealthAnalysisResponse, HealthAnalysisListItem, HealthAnalysisContext } from '../api/agent';
import type { AgentDocument, AgentMemory, DocumentDetail, MemoryHistoryItem } from '../api/agent';
import type { UserDetail } from '../api/users';
import type { Tier } from '../api/tiers';

// ── Utilities ──────────────────────────────────────────────

export type TabId = 'chat' | 'info' | 'thread' | 'memories' | 'documents' | 'health' | 'calendar';

export const getTierStyle = (tierId: string | null): string => {
  const id = tierId?.toLowerCase() || 'free';
  if (id.includes('pro')) return 'bg-purple-100 text-purple-700';
  if (id.includes('basic')) return 'bg-blue-100 text-blue-700';
  if (id.includes('lifetime')) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

// ── TabBar ─────────────────────────────────────────────────

interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  memoriesCount: number;
  documentsCount: number;
  selectedUserId: string | null;
  selectedThreadId: string | null;
}

export const TabBar: React.FC<TabBarProps> = ({
  activeTab,
  onTabChange,
  memoriesCount,
  documentsCount,
  selectedUserId,
  selectedThreadId,
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const tabClass = (tab: TabId) =>
    `py-3 px-1 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
      activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
    }`;

  return (
    <div className="border-b border-slate-200 bg-slate-50">
      <div className="px-6 flex items-center justify-between">
        <nav className="flex gap-6">
          <button onClick={() => onTabChange('chat')} data-testid="tab-chat" className={tabClass('chat')}>
            <MessageSquare size={16} />
            채팅
          </button>
          <button onClick={() => onTabChange('info')} data-testid="tab-info" className={tabClass('info')}>
            <Info size={16} />
            사용자 정보
          </button>
          <button
            onClick={() => selectedThreadId && onTabChange('thread')}
            data-testid="tab-thread"
            className={`${tabClass('thread')} ${!selectedThreadId ? 'opacity-40 pointer-events-none' : ''}`}
          >
            <Hash size={16} />
            스레드
          </button>
          <button onClick={() => onTabChange('memories')} data-testid="tab-memories" className={tabClass('memories')}>
            <Brain size={16} />
            메모리 ({memoriesCount})
          </button>
          <button
            onClick={() => onTabChange('documents')}
            data-testid="tab-documents"
            className={tabClass('documents')}
          >
            <FileText size={16} />
            문서 ({documentsCount})
          </button>
          <button onClick={() => onTabChange('health')} data-testid="tab-health" className={tabClass('health')}>
            <Activity size={16} />
            건강 분석
          </button>
          <button onClick={() => onTabChange('calendar')} data-testid="tab-calendar" className={tabClass('calendar')}>
            <CalendarDays size={16} />
            캘린더
          </button>
        </nav>
      </div>
      <div className="px-6 pb-2 flex items-center gap-2 text-[11px] text-slate-400 font-mono">
        <span
          className={`px-2 py-0.5 rounded cursor-pointer select-all transition-colors ${copiedId === 'user' ? 'bg-green-100 text-green-600' : 'bg-slate-100 hover:bg-slate-200'}`}
          title="클릭하여 복사"
          onClick={() => {
            copyText(selectedUserId || '');
            setCopiedId('user');
            setTimeout(() => setCopiedId(null), 1500);
          }}
        >
          {copiedId === 'user' ? (
            <>
              <Check size={10} className="inline mr-1" />
              복사됨
            </>
          ) : (
            <>user: {selectedUserId}</>
          )}
        </span>
        {selectedThreadId && (
          <span
            className={`px-2 py-0.5 rounded cursor-pointer select-all transition-colors ${copiedId === 'thread' ? 'bg-green-100 text-green-600' : 'bg-slate-100 hover:bg-slate-200'}`}
            title="클릭하여 복사"
            onClick={() => {
              copyText(selectedThreadId || '');
              setCopiedId('thread');
              setTimeout(() => setCopiedId(null), 1500);
            }}
          >
            {copiedId === 'thread' ? (
              <>
                <Check size={10} className="inline mr-1" />
                복사됨
              </>
            ) : (
              <>thread: {selectedThreadId}</>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

// ── ErrorBanner ────────────────────────────────────────────

interface ErrorBannerProps {
  error: string | null;
  onDismiss: () => void;
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({ error, onDismiss }) => {
  if (!error) return null;
  return (
    <div className="mx-4 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
      <AlertCircle size={16} />
      <span>{error}</span>
      <button onClick={onDismiss} className="ml-auto text-red-500 hover:text-red-700">
        &times;
      </button>
    </div>
  );
};

// ── ThreadInfoPanel ────────────────────────────────────────

export interface ThreadInfoPanelProps {
  threadDetail: ThreadDetail | null;
  threadDetailLoading: boolean;
  threadStats: ThreadStats | null;
  threadSummary: string | null;
  generationStatus: string | null;
  archiveTotal: number;
  onRefresh: () => void;
}

const generationBadge = (status: string | null) => {
  switch (status) {
    case 'generating':
      return 'bg-yellow-100 text-yellow-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-green-100 text-green-700';
  }
};

export const ThreadInfoPanel: React.FC<ThreadInfoPanelProps> = ({
  threadDetail,
  threadDetailLoading,
  threadStats,
  threadSummary,
  generationStatus,
  archiveTotal,
  onRefresh,
}) => {
  if (threadDetailLoading && !threadDetail) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 size={24} className="animate-spin mr-2" />
        스레드 정보 로딩 중...
      </div>
    );
  }

  if (!threadDetail) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        스레드를 선택해주세요.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-200px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">스레드 상세 정보</h3>
        <button
          onClick={onRefresh}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          title="새로고침"
        >
          <RefreshCw size={16} className={threadDetailLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div>
        <div className="text-xs text-slate-400 mb-1">제목</div>
        <div className="text-base font-medium text-slate-700">{threadDetail.title || '(제목 없음)'}</div>
      </div>

      {threadStats && (
        <div>
          <h4 className="text-sm font-medium text-slate-600 mb-3">대화 통계</h4>
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-indigo-50 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-indigo-600">
                {threadStats.activeTurns}
                <span className="text-sm font-normal text-indigo-400">/{threadStats.totalTurns}</span>
              </div>
              <div className="text-xs text-indigo-500 mt-1">활성/전체 턴</div>
            </div>
            <div className="bg-cyan-50 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-cyan-600">{threadStats.activeTokens.toLocaleString()}</div>
              <div className="text-xs text-cyan-500 mt-1">활성 토큰</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-amber-600">{threadStats.idleHours}</div>
              <div className="text-xs text-amber-500 mt-1">유휴 시간(h)</div>
            </div>
            <div className={`rounded-lg p-4 text-center ${generationBadge(generationStatus).replace('text-', 'bg-').split(' ')[0]} bg-opacity-30`}>
              <div className="text-xl font-bold flex items-center justify-center">
                {generationStatus === 'generating' && <Loader2 size={16} className="animate-spin mr-1" />}
                <span className={generationBadge(generationStatus).split(' ')[1]}>{generationStatus || 'idle'}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">생성 상태</div>
            </div>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-slate-600 mb-2">대화 요약</h4>
        {threadSummary ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 whitespace-pre-wrap">{threadSummary}</div>
        ) : (
          <div className="text-sm text-slate-400 italic">요약이 아직 생성되지 않았습니다.</div>
        )}
      </div>

      <div>
        <h4 className="text-sm font-medium text-slate-600 mb-3">메시지 통계</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-xl font-bold text-blue-600">{threadDetail.message_count}</div>
            <div className="text-xs text-blue-500 mt-1">활성 메시지</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <div className="text-xl font-bold text-slate-600">{archiveTotal}</div>
            <div className="text-xs text-slate-500 mt-1">아카이브 메시지</div>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-slate-600 mb-3">타임스탬프</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Clock size={14} className="text-slate-400" />
            <span className="text-slate-400 w-24">생성일</span>
            <span>{formatDateKo(threadDetail.created_at)}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Clock size={14} className="text-slate-400" />
            <span className="text-slate-400 w-24">수정일</span>
            <span>{formatDateKo(threadDetail.updated_at)}</span>
          </div>
          {threadDetail.last_message_at && (
            <div className="flex items-center gap-2 text-slate-600">
              <Clock size={14} className="text-slate-400" />
              <span className="text-slate-400 w-24">마지막 메시지</span>
              <span>{formatDateKo(threadDetail.last_message_at)}</span>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// ── InfoTabPanel ───────────────────────────────────────────

export interface ProfileEditorState {
  isEditingProfile: boolean;
  editProfileJson: string;
  isSavingProfile: boolean;
  setEditProfileJson: (json: string) => void;
  startEditingProfile: () => void;
  cancelEditingProfile: () => void;
  handleSaveProfile: () => void;
}

export interface TierManagerState {
  availableTiers: Tier[];
  showTierDropdown: boolean;
  isChangingTier: boolean;
  setShowTierDropdown: (show: boolean) => void;
  handleChangeTier: (tierId: string) => void;
}

interface InfoTabPanelProps {
  user: UserDetail;
  profile: ProfileEditorState;
  tier: TierManagerState;
  extraProfileActions?: React.ReactNode;
  extraSections?: React.ReactNode;
  /**
   * BE에서 저장하는 thread metadata (사용자 컨텍스트). 선택된 thread가 있을 때만 표시.
   * 기존 ThreadInfoPanel 메타데이터 섹션을 사용자 정보 탭으로 이동.
   */
  threadMetadata?: Record<string, unknown> | null;
}

export const InfoTabPanel: React.FC<InfoTabPanelProps> = ({
  user,
  profile,
  tier,
  extraProfileActions,
  extraSections,
  threadMetadata,
}) => (
  <div className="flex-1 overflow-y-auto p-6">
    <div className="space-y-6">
      {/* Header with Edit Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">사용자 상세 정보</h3>
        {!profile.isEditingProfile ? (
          <button
            onClick={profile.startEditingProfile}
            data-testid="edit-profile-button"
            className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-1"
          >
            <Edit2 size={14} />
            프로필 편집
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={profile.cancelEditingProfile}
              className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              취소
            </button>
            <button
              onClick={profile.handleSaveProfile}
              disabled={profile.isSavingProfile}
              data-testid="save-profile-button"
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
            >
              {profile.isSavingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              저장
            </button>
          </div>
        )}
      </div>

      {/* User Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-slate-50 rounded-lg p-5 space-y-4">
            <div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">사용자 ID</span>
              <div className="font-mono text-sm text-slate-700 mt-1 break-all bg-white p-2 rounded border">
                {user.user_id}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="relative">
                <span className="text-xs text-slate-400 uppercase tracking-wider">티어</span>
                <div className="mt-1">
                  <button
                    onClick={() => tier.setShowTierDropdown(!tier.showTierDropdown)}
                    data-testid="tier-dropdown-button"
                    disabled={tier.isChangingTier}
                    className={`px-3 py-1 rounded text-sm inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity ${getTierStyle(
                      user.tier?.id,
                    )} ${tier.isChangingTier ? 'opacity-50' : ''}`}
                  >
                    {tier.isChangingTier ? <Loader2 size={12} className="animate-spin" /> : null}
                    {user.tier?.id || 'free'}
                    <ChevronDown size={12} />
                  </button>
                  {tier.showTierDropdown && (
                    <div
                      data-testid="tier-dropdown-menu"
                      className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]"
                    >
                      {tier.availableTiers.map((tierItem) => (
                        <button
                          key={tierItem.id}
                          onClick={() => tier.handleChangeTier(tierItem.id)}
                          data-testid={`tier-option-${tierItem.id}`}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                            user.tier?.id === tierItem.id ? 'bg-slate-100' : ''
                          }`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${
                              tierItem.id?.toLowerCase().includes('pro')
                                ? 'bg-purple-500'
                                : tierItem.id?.toLowerCase().includes('basic')
                                  ? 'bg-blue-500'
                                  : tierItem.id?.toLowerCase().includes('lifetime')
                                    ? 'bg-amber-500'
                                    : 'bg-slate-400'
                            }`}
                          />
                          {tierItem.config?.name || tierItem.id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">생성일</span>
                <div className="text-slate-700 mt-1">{formatDateKo(user.created_at)}</div>
              </div>
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">마지막 활동</span>
                <div className="text-slate-700 mt-1">{formatDateKo(user.updated_at)}</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-slate-600 mb-3">사용량</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-5 text-center">
              <div className="text-3xl font-bold text-blue-600">{user.usage?.memory_count || 0}</div>
              <div className="text-sm text-blue-500 mt-2">메모리</div>
            </div>
            <div className="bg-green-50 rounded-lg p-5 text-center">
              <div className="text-3xl font-bold text-green-600">{user.usage?.document_count || 0}</div>
              <div className="text-sm text-green-500 mt-2">문서</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-5 text-center">
              <div className="text-3xl font-bold text-purple-600">{user.usage?.thread_count || 0}</div>
              <div className="text-sm text-purple-500 mt-2">스레드</div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile JSON */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-700">프로필 데이터 (user, device, custom, settings)</h4>
          {extraProfileActions}
        </div>
        {profile.isEditingProfile ? (
          <textarea
            value={profile.editProfileJson}
            onChange={(e) => profile.setEditProfileJson(e.target.value)}
            data-testid="edit-profile-json"
            className="w-full h-[480px] bg-slate-900 text-green-400 p-4 rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
            spellCheck={false}
          />
        ) : (
          <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm overflow-auto h-[480px]">
            {JSON.stringify(
              {
                user: user.user || {},
                device: user.device || {},
                custom: user.custom || {},
                settings: user.settings || {},
              },
              null,
              2,
            )}
          </pre>
        )}
      </div>

      {/* Identity Profile (read-only, LLM auto-accumulated) */}
      {(() => {
        const identity = user.identity_profile ?? {};
        const isEmpty = Object.keys(identity).length === 0;
        return (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Lock size={14} className="text-slate-500" />
              <h4 className="font-semibold text-slate-700">Identity Profile</h4>
              <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">read-only</span>
              {isEmpty && (
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                  비어 있음 (대화 진행 후 LLM이 누적)
                </span>
              )}
            </div>
            <pre
              data-testid="identity-profile-json"
              className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm overflow-auto max-h-[320px]"
            >
              {JSON.stringify(identity, null, 2)}
            </pre>
          </div>
        );
      })()}

      {/* Thread metadata (BE 저장 사용자 컨텍스트) — 선택된 thread가 있을 때 항상 표시.
          외부 SNS 가입자만 user_info가 채워지고 playground 유저는 {}이지만,
          어드민 디버깅 일관성을 위해 빈 상태도 배지로 노출. */}
      {threadMetadata !== null && threadMetadata !== undefined && (() => {
        const isEmpty = Object.keys(threadMetadata).length === 0;
        return (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h4 className="font-semibold text-slate-700">메타데이터 (현재 스레드)</h4>
              {isEmpty && (
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                  비어 있음 (외부 SNS 가입 정보 없음 — playground·테스트 유저)
                </span>
              )}
            </div>
            <pre
              data-testid="thread-metadata-json"
              className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 overflow-x-auto"
            >
              {JSON.stringify(threadMetadata, null, 2)}
            </pre>
          </div>
        );
      })()}

      {/* Extra sections (e.g. tools grid in PlaygroundChat) */}
      {extraSections}
    </div>
  </div>
);

// ── MemoriesTabPanel ───────────────────────────────────────

const memoryEventBadge = (event: string) => {
  switch (event) {
    case 'ADD':
      return 'bg-green-100 text-green-700';
    case 'UPDATE':
      return 'bg-blue-100 text-blue-700';
    case 'DELETE':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

interface MemoriesTabPanelProps {
  memories: AgentMemory[];
  loading: boolean;
  onRefresh: () => void;
  onDelete: (memoryId: string) => void;
  onUpdate?: (memoryId: string, content: string) => void;
  history?: MemoryHistoryItem[];
  historyLoading?: boolean;
  onLoadHistory?: () => void;
}

export const MemoriesTabPanel: React.FC<MemoriesTabPanelProps> = ({
  memories,
  loading,
  onRefresh,
  onDelete,
  onUpdate,
  history,
  historyLoading,
  onLoadHistory,
}) => {
  const [view, setView] = useState<'memories' | 'history'>('memories');
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleEditStart = (memory: AgentMemory) => {
    setEditingMemoryId(memory.memory_id);
    setEditingContent(memory.content);
  };

  const handleEditSave = async () => {
    if (!onUpdate || !editingMemoryId || !editingContent.trim()) return;
    setIsSaving(true);
    try {
      await onUpdate(editingMemoryId, editingContent.trim());
      setEditingMemoryId(null);
      setEditingContent('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingMemoryId(null);
    setEditingContent('');
  };

  const handleViewChange = (newView: 'memories' | 'history') => {
    setView(newView);
    if (newView === 'history' && onLoadHistory) {
      onLoadHistory();
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewChange('memories')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
              view === 'memories'
                ? 'bg-blue-100 text-blue-700'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Brain size={14} />
            메모리
          </button>
          <button
            onClick={() => handleViewChange('history')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
              view === 'history'
                ? 'bg-blue-100 text-blue-700'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <History size={14} />
            변경 이력
          </button>
        </div>
        <button
          onClick={() => {
            onRefresh();
            if (view === 'history' && onLoadHistory) onLoadHistory();
          }}
          disabled={loading}
          className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          새로고침
        </button>
      </div>

      {/* Memories View */}
      {view === 'memories' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : memories.length === 0 ? (
            <div className="text-center text-slate-400 py-16">
              <Brain size={48} className="mx-auto mb-3 opacity-50" />
              <p>저장된 메모리가 없습니다</p>
              <p className="text-xs mt-2">대화 중 중요한 정보는 자동으로 메모리에 저장됩니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {memories.map((memory) => (
                <div
                  key={memory.memory_id}
                  className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    {editingMemoryId === memory.memory_id ? (
                      <div className="flex-1">
                        <textarea
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none"
                        />
                        <div className="flex justify-end gap-1 mt-2">
                          <button
                            onClick={handleEditSave}
                            disabled={isSaving || !editingContent.trim()}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                            title="저장"
                          >
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"
                            title="취소"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-slate-700 leading-relaxed flex-1">{memory.content}</p>
                        <div className="flex gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {onUpdate && (
                            <button
                              onClick={() => handleEditStart(memory)}
                              className="p-1 text-slate-300 hover:text-blue-500"
                              title="수정"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(memory.memory_id)}
                            className="p-1 text-slate-300 hover:text-red-500"
                            title="삭제"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {editingMemoryId !== memory.memory_id && memory.tags && memory.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {memory.tags.map((tag, i) => (
                        <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {editingMemoryId !== memory.memory_id && (
                    <div className="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 space-y-1">
                      <div className="font-mono break-all">{memory.memory_id}</div>
                      <div>{formatDateKo(memory.created_at)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* History View */}
      {view === 'history' && (
        <>
          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : !history || history.length === 0 ? (
            <div className="text-center text-slate-400 py-16">
              <History size={48} className="mx-auto mb-3 opacity-50" />
              <p>변경 이력이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="bg-white p-4 rounded-lg shadow-sm border border-slate-200"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${memoryEventBadge(item.event)}`}>
                      {item.event}
                    </span>
                    <span className="text-xs text-slate-400">{formatDateKo(item.created_at)}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{item.source}</span>
                  </div>
                  <div className="text-xs font-mono text-slate-400 mb-2 space-y-0.5">
                    <div>history: {item.id}</div>
                    <div>memory: {item.memory_id}</div>
                  </div>

                  {item.event === 'ADD' && item.new_content && (
                    <p className="text-sm text-slate-600 pl-2 border-l-2 border-green-300">{item.new_content}</p>
                  )}

                  {item.event === 'UPDATE' && (
                    <div className="flex items-start gap-2 text-sm">
                      <p className="text-slate-400 line-through flex-1 pl-2 border-l-2 border-slate-200">
                        {item.old_content}
                      </p>
                      <ArrowRight size={14} className="text-slate-300 mt-1 shrink-0" />
                      <p className="text-slate-600 flex-1 pl-2 border-l-2 border-blue-300">{item.new_content}</p>
                    </div>
                  )}

                  {item.event === 'DELETE' && item.old_content && (
                    <p className="text-sm text-slate-400 line-through pl-2 border-l-2 border-red-300">
                      {item.old_content}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── DocumentsTabPanel ──────────────────────────────────────

interface DocumentsTabPanelProps {
  documents: AgentDocument[];
  loading: boolean;
  onRefresh: () => void;
  onDelete: (documentId: string) => void;
  onToggle?: (documentId: string) => void;
  expandedDocumentId?: string | null;
  expandedDocumentDetail?: DocumentDetail | null;
  loadingDocumentDetail?: boolean;
  onToggleDetail?: (documentId: string) => void;
  headerExtra?: React.ReactNode;
  beforeList?: React.ReactNode;
}

export const DocumentsTabPanel: React.FC<DocumentsTabPanelProps> = ({
  documents,
  loading,
  onRefresh,
  onDelete,
  onToggle,
  expandedDocumentId,
  expandedDocumentDetail,
  loadingDocumentDetail,
  onToggleDetail,
  headerExtra,
  beforeList,
}) => (
  <div className="flex-1 overflow-y-auto p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-slate-800">문서 (RAG)</h3>
      <div className="flex items-center gap-2">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          새로고침
        </button>
        {headerExtra}
      </div>
    </div>

    {beforeList}

    {/* Documents List */}
    {loading ? (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-slate-400" />
      </div>
    ) : documents.length === 0 ? (
      <div className="text-center text-slate-400 py-16">
        <FileText size={48} className="mx-auto mb-3 opacity-50" />
        <p>업로드된 문서가 없습니다</p>
      </div>
    ) : (
      <div className="space-y-3">
        {documents.map((doc) => {
          const docId = String(doc.id);
          const isExpanded = expandedDocumentId === docId;
          const detail = isExpanded ? expandedDocumentDetail : null;

          return (
            <div
              key={docId}
              className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div
                    className="flex items-start gap-3 flex-1 cursor-pointer"
                    onClick={() => onToggleDetail?.(docId)}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        doc.status === 'ready'
                          ? 'bg-green-100 text-green-600'
                          : doc.status === 'processing'
                            ? 'bg-yellow-100 text-yellow-600'
                            : doc.status === 'error'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <File size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-800 truncate">{doc.file.name}</h4>
                        {isExpanded ? (
                          <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="text-xs font-mono text-slate-400 mt-1 break-all">{docId}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span>{(doc.file.size / 1024).toFixed(1)} KB</span>
                        <span>{doc.file.type}</span>
                        <span
                          className={`px-1.5 py-0.5 rounded ${
                            doc.status === 'ready'
                              ? 'bg-green-100 text-green-700'
                              : doc.status === 'processing'
                                ? 'bg-yellow-100 text-yellow-700'
                                : doc.status === 'error'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {doc.status}
                        </span>
                      </div>
                      {!isExpanded && doc.summary && (
                        <p className="text-sm text-slate-500 mt-2 line-clamp-2">{doc.summary}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>{formatDateKo(doc.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {/* is_active toggle */}
                    {onToggle && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(docId);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          doc.is_active ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        title={doc.is_active ? '활성 (클릭하여 비활성화)' : '비활성 (클릭하여 활성화)'}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            doc.is_active ? 'translate-x-4.5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(docId);
                      }}
                      className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                      title="삭제"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-slate-100 p-5 bg-slate-50">
                  {loadingDocumentDetail ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 size={20} className="animate-spin text-slate-400" />
                    </div>
                  ) : detail ? (
                    <div className="space-y-4">
                      {/* Summary */}
                      {detail.summary && (
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                            <FileText size={14} /> 요약
                          </h5>
                          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                            {detail.summary}
                          </p>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>총 {detail.total_chunks}개 청크</span>
                        <span>
                          {detail.chunks.reduce((acc, c) => acc + c.content.length, 0).toLocaleString()}자
                        </span>
                        <span>검색 {detail.stats.search_count}회</span>
                        {detail.stats.last_searched_at && (
                          <span>최근 검색: {formatDateKo(detail.stats.last_searched_at)}</span>
                        )}
                      </div>

                      {/* Chunks preview */}
                      {detail.chunks.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">
                            청크 미리보기 (상위 {Math.min(detail.chunks.length, 5)}개)
                          </h5>
                          <div className="space-y-2">
                            {detail.chunks.slice(0, 5).map((chunk) => (
                              <div
                                key={chunk.index}
                                className="bg-white border border-slate-200 rounded p-3"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-slate-500">
                                    #{chunk.index}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {chunk.content.length}자
                                  </span>
                                </div>
                                <p className="text-xs text-slate-600 line-clamp-3 whitespace-pre-line">
                                  {chunk.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">
                      문서 상세 정보를 불러올 수 없습니다.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ── HealthAnalysisDetail (internal) ────────────────────────

const HealthAnalysisDetailView: React.FC<{
  detail: HealthAnalysisResponse;
  loadingDetail: boolean;
}> = ({ detail, loadingDetail }) => {
  if (loadingDetail) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (detail?.result) {
    return (
      <div className="space-y-5">
        {/* Medical Attention Banner */}
        {detail.result.medical_attention_needed && (
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">의료 주의 필요</p>
              {detail.result.medical_attention_reason && (
                <p className="text-sm text-red-700 mt-1">{detail.result.medical_attention_reason}</p>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {detail.result.analysis?.summary && detail.result.analysis.summary.length > 0 && (
          <div>
            <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <FileText size={14} /> 요약
            </h5>
            <ul className="space-y-1">
              {detail.result.analysis.summary.map((item, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-slate-400 mt-1">&#8226;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Assessment */}
        {detail.result.analysis?.assessment && (
          <div>
            <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Activity size={14} /> 평가
            </h5>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {detail.result.analysis.assessment}
            </p>
          </div>
        )}

        {/* Positive Aspects */}
        {detail.result.analysis?.positive_aspects && detail.result.analysis.positive_aspects.length > 0 && (
          <div>
            <h5 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1.5">
              <Check size={14} /> 긍정적 측면
            </h5>
            <ul className="space-y-1">
              {detail.result.analysis.positive_aspects.map((item, i) => (
                <li key={i} className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded flex items-start gap-2">
                  <Check size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Areas of Concern */}
        {detail.result.analysis?.areas_of_concern && detail.result.analysis.areas_of_concern.length > 0 && (
          <div>
            <h5 className="text-sm font-semibold text-yellow-700 mb-2 flex items-center gap-1.5">
              <AlertCircle size={14} /> 주의 영역
            </h5>
            <ul className="space-y-1">
              {detail.result.analysis.areas_of_concern.map((item, i) => (
                <li key={i} className="text-sm text-yellow-800 bg-yellow-50 px-3 py-1.5 rounded flex items-start gap-2">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {detail.result.analysis?.recommendations && detail.result.analysis.recommendations.length > 0 && (
          <div>
            <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Sparkles size={14} /> 권장 사항
            </h5>
            <div className="space-y-2">
              {detail.result.analysis.recommendations.map((rec, i) => (
                <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                      {rec.category === 'exercise'
                        ? '운동'
                        : rec.category === 'diet'
                          ? '식단'
                          : rec.category === 'sleep'
                            ? '수면'
                            : rec.category === 'lifestyle'
                              ? '생활습관'
                              : rec.category === 'medical'
                                ? '의료'
                                : rec.category}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{rec.action}</p>
                  <p className="text-xs text-slate-500 mt-1">{rec.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Questions */}
        {detail.result.suggested_questions && detail.result.suggested_questions.length > 0 && (
          <div>
            <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <MessageSquare size={14} /> 후속 질문
            </h5>
            <div className="flex flex-wrap gap-2">
              {detail.result.suggested_questions.map((q, i) => (
                <span key={i} className="text-xs bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full">
                  {q}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {detail.result.disclaimer && (
          <p className="text-xs text-slate-400 italic border-t border-slate-100 pt-3">{detail.result.disclaimer}</p>
        )}

        {/* Input Data Section (collapsible) */}
        {detail.context && (
          <details className="border-t border-slate-100 pt-3">
            <summary className="text-sm font-medium text-slate-500 cursor-pointer hover:text-slate-700 flex items-center gap-1.5">
              <Info size={14} /> 입력 데이터
            </summary>
            <div className="mt-3 space-y-3">
              {(() => {
                const ctx = detail.context as HealthAnalysisContext;
                const requestData = ctx.request || (!ctx.user_profile ? detail.context : null);
                return requestData ? (
                  <div>
                    <h6 className="text-xs font-medium text-slate-500 mb-1">요청 데이터</h6>
                    <pre className="bg-slate-50 text-slate-700 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(requestData, null, 2)}
                    </pre>
                  </div>
                ) : null;
              })()}
              {(() => {
                const ctx = detail.context as HealthAnalysisContext;
                return ctx.user_profile && Object.keys(ctx.user_profile).length > 0 ? (
                  <div>
                    <h6 className="text-xs font-medium text-slate-500 mb-1">사용자 프로필</h6>
                    <pre className="bg-slate-50 text-slate-700 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(ctx.user_profile, null, 2)}
                    </pre>
                  </div>
                ) : null;
              })()}
              {(() => {
                const ctx = detail.context as HealthAnalysisContext;
                return ctx.memories && ctx.memories.length > 0 ? (
                  <div>
                    <h6 className="text-xs font-medium text-slate-500 mb-1">장기 기억 ({ctx.memories.length}건)</h6>
                    <div className="space-y-1">
                      {ctx.memories.map((mem, i) => (
                        <div key={i} className="bg-slate-50 px-3 py-2 rounded text-xs">
                          <p className="text-slate-700">{mem.content}</p>
                          {mem.tags && mem.tags.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {mem.tags.map((tag, j) => (
                                <span key={j} className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </details>
        )}
      </div>
    );
  }

  if (detail?.error) {
    return (
      <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
        <p className="font-medium">{detail.error.code}</p>
        <p className="text-xs mt-1">{detail.error.detail}</p>
      </div>
    );
  }

  return <p className="text-sm text-slate-400 text-center py-4">분석 결과를 불러올 수 없습니다.</p>;
};

// ── HealthTabPanel ─────────────────────────────────────────

interface HealthTabPanelProps {
  analyses: HealthAnalysisListItem[];
  loading: boolean;
  expandedAnalysisId: string | null;
  expandedAnalysisDetail: HealthAnalysisResponse | null;
  loadingAnalysisDetail: boolean;
  onRefresh: () => void;
  onToggleDetail: (analysisId: string) => void;
  onDelete?: (analysisId: string) => void;
  beforeList?: React.ReactNode;
  emptyMessage?: string;
}

export const HealthTabPanel: React.FC<HealthTabPanelProps> = ({
  analyses,
  loading,
  expandedAnalysisId,
  expandedAnalysisDetail,
  loadingAnalysisDetail,
  onRefresh,
  onToggleDetail,
  onDelete,
  beforeList,
  emptyMessage,
}) => (
  <div className="flex-1 overflow-y-auto p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-slate-800">건강 분석</h3>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-1 disabled:opacity-50"
      >
        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        새로고침
      </button>
    </div>

    {beforeList}

    {/* Analysis Results */}
    {loading ? (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-slate-400" />
      </div>
    ) : analyses.length === 0 ? (
      <div className="text-center text-slate-400 py-16">
        <Activity size={48} className="mx-auto mb-3 opacity-50" />
        <p>건강 분석 결과가 없습니다</p>
        {emptyMessage && <p className="text-xs mt-2">{emptyMessage}</p>}
      </div>
    ) : (
      <div className="space-y-3" data-testid="health-analysis-list">
        {analyses.map((analysis) => {
          const isExpanded = expandedAnalysisId === analysis.analysis_id;
          const detail = isExpanded ? expandedAnalysisDetail : null;
          const flag = analysis.result?.flag;
          const title = analysis.result?.title;

          return (
            <div
              key={analysis.analysis_id}
              className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
            >
              {/* Card Header */}
              <button
                onClick={() => onToggleDetail(analysis.analysis_id)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid={`analysis-card-${analysis.analysis_id}`}
              >
                <div className="flex items-center gap-3">
                  {flag && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        flag === 'green'
                          ? 'bg-green-100 text-green-700'
                          : flag === 'yellow'
                            ? 'bg-yellow-100 text-yellow-700'
                            : flag === 'red'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <Flag size={12} />
                      {flag === 'green' ? '양호' : flag === 'yellow' ? '주의' : flag === 'red' ? '경고' : flag}
                    </span>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-800">
                      {title ||
                        (analysis.status === 'completed'
                          ? '분석 완료'
                          : analysis.status === 'processing'
                            ? '분석 중...'
                            : analysis.status === 'failed'
                              ? '분석 실패'
                              : '대기 중')}
                    </p>
                    <p className="text-xs text-slate-400">{formatDateKo(analysis.created_at)}</p>
                    <p className="text-xs font-mono text-slate-400 break-all">{analysis.analysis_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {analysis.status === 'processing' && <Loader2 size={14} className="animate-spin text-yellow-500" />}
                  {analysis.status === 'failed' && <AlertCircle size={14} className="text-red-500" />}
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(analysis.analysis_id);
                      }}
                      className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
                      title="삭제"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={16} className="text-slate-400" />
                  )}
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-slate-100 p-5">
                  <HealthAnalysisDetailView detail={detail!} loadingDetail={loadingAnalysisDetail} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);
